import React from "react";
import { Church, X, CalendarClock, ArrowRight, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { BottomSheet, MentionSearch } from "./StorySheets";
import { AudienceOptions } from "@/components/AudienceOptions";
import { StoryHiddenFromLink } from "./StoryHiddenFrom";
import { audienceOf } from "@/lib/postAudience";
import { imageVariant } from "@/lib/mediaUrl";
import { maxDatetimeLocal } from "@/lib/scheduleLimits";

const minDatetimeLocal = () => {
  const d = new Date(Date.now() + 5 * 60000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const destRow = (on) => `w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left text-sm font-semibold border transition-colors ${on ? "bg-white/15 border-white/40" : "bg-white/5 border-transparent"}`;

// Feuille « Partager » : destination (moi / église), audience (lot 651, « Ma story » seulement), mentions, programmation, puis Partager.
export const StoryShareSheet = ({ open, onClose, user, churches, dest, setDest, mandatory, audience = "public", setAudience, onHiddenFrom, onCloseFriends, mentions, setMentions, scheduledAt, setScheduledAt, onShare, onPreview, busy, t }) => {
  const initial = (user?.name || "?").trim()[0]?.toUpperCase();
  const scheduled = !!scheduledAt;
  const personal = !dest || dest === "me";
  const aud = audienceOf(audience);
  return (
    <BottomSheet open={open} onClose={onClose} title={t("story.share_sheet_title", "Partager la story")} testId="story-fs-share-sheet" maxH="85dvh">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60 mb-1.5">{t("story.destination", "Destination")}</div>
      <div className="space-y-1.5" data-testid="story-fs-share-destinations">
        <button type="button" onClick={() => setDest("me")} aria-pressed={dest === "me"} className={destRow(dest === "me")} data-testid="story-fs-dest-me">
          {user?.avatar_url
            ? <img src={imageVariant(user.avatar_url, 48)} alt="" className="h-9 w-9 rounded-full object-cover" />
            : <span className="h-9 w-9 rounded-full grid place-items-center text-white text-sm font-bold" style={{ background: "var(--yalola-primary)" }}>{initial}</span>}
          <span className="min-w-0"><span className="block truncate">{t("story.my_story", "Ma story")}</span><span className="block text-[11px] font-normal opacity-60 truncate inline-flex items-center gap-1"><aud.icon size={11} /> {t(aud.tkey, aud.label)}</span></span>
        </button>
        {churches.map((c) => (
          <button key={c.id} type="button" onClick={() => setDest(c.id)} aria-pressed={dest === c.id} className={destRow(dest === c.id)} data-testid={`story-fs-dest-${c.id}`}>
            <span className="h-9 w-9 rounded-full grid place-items-center bg-sky-500 text-white shrink-0"><Church size={16} /></span>
            <span className="min-w-0"><span className="block truncate">{c.name}</span><span className="block text-[11px] font-normal opacity-60 truncate">{t("story.church_story", "Église")}{c.city ? ` · ${c.city}` : ""}</span></span>
          </button>
        ))}
      </div>
      {mandatory && !dest && <div className="text-[11px] mt-1.5" style={{ color: "#FDBA74" }} data-testid="story-fs-dest-hint">{t("story.no_dest", "Choisis une destination")}</div>}

      {personal && setAudience && (
        <div data-testid="story-fs-audience" data-audience={audience}>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60 mt-4 mb-1.5">{t("audience.story_title", "Qui peut voir ta story ?")}</div>
          <AudienceOptions value={audience} onPick={setAudience} t={t} tone="dark" onManageCloseFriends={onCloseFriends} />
          {onHiddenFrom && audience !== "private" && <div className="mt-2"><StoryHiddenFromLink onClick={onHiddenFrom} tone="dark" testId="story-fs-hidden-link" /></div>}
        </div>
      )}

      <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60 mt-4 mb-1.5">{t("story.mention", "Mentionner des membres")}</div>
      {mentions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="story-fs-mention-chips">
          {mentions.map((m) => (
            <span key={m.id} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold bg-white/15" data-testid={`story-fs-mention-chip-${m.id}`}>
              @{m.name}<button type="button" onClick={() => setMentions((arr) => arr.filter((x) => x.id !== m.id))} aria-label={t("common.delete", "Supprimer")}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <MentionSearch onPick={(u) => setMentions((arr) => (arr.some((m) => m.id === u.id) ? arr : [...arr, { id: u.id, name: u.name }].slice(0, 10)))} exclude={mentions.map((m) => m.id)} t={t} />

      <div className="flex items-center justify-between mt-4">
        <span className="text-sm font-semibold flex items-center gap-1.5"><CalendarClock size={15} style={{ color: "var(--yalola-primary)" }} /> {t("story.schedule", "Programmer la publication")}</span>
        <Switch checked={scheduled} onCheckedChange={(v) => setScheduledAt(v ? minDatetimeLocal() : "")} data-testid="story-fs-share-schedule-switch" />
      </div>
      {scheduled && (
        <input type="datetime-local" value={scheduledAt} min={minDatetimeLocal()} max={maxDatetimeLocal()} onChange={(e) => setScheduledAt(e.target.value)} className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none bg-white/10 border-white/15 text-white" style={{ colorScheme: "dark" }} data-testid="story-fs-share-schedule-input" />
      )}

      <div className="mt-5 flex gap-2">
        {onPreview && (
          <button type="button" onClick={onPreview} className="h-12 px-4 rounded-full bg-white/10 border border-white/20 text-white text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform shrink-0" data-testid="story-fs-share-preview">
            <Eye size={16} /> {t("story.preview", "Aperçu")}
          </button>
        )}
        <button
          type="button"
          onClick={() => onShare(dest)}
          disabled={busy || (mandatory && !dest)}
          className="flex-1 h-12 rounded-full text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
          style={{ background: "var(--yalola-primary)" }}
          data-testid="story-fs-share-submit"
        >
          {busy ? t("story.publishing", "Publication…") : (scheduled ? t("story.schedule", "Programmer la publication") : t("story.publish_now", "Partager"))} <ArrowRight size={16} />
        </button>
      </div>
    </BottomSheet>
  );
};
